import { Table, Button, Pagination, Select, Label, ButtonGroup } from "flowbite-react";
import { HiPencil, HiPlus, HiTrash } from "react-icons/hi";
import { useNavigate } from 'react-router-dom';
import { useEffect, useRef } from "react";
import useUsers from "../hooks/useUsers";
import { routes } from "../routes";
import { useReducerAsState } from "../hooks/useReducerAsState";
import useProfiles from "../hooks/useProfiles";
import { TableResults } from "../components";
import { toast } from "react-toastify";
import { useAuth } from "../context/authContext";

interface State {
    profileId: number;
    active: number;
    page: number;
    limit: number;
}

const UserPage = () => {
    const { permissions } = useAuth()

    const canCreate = permissions.find(item => item.slug === 'crear-usuarios')
    const canEdit = permissions.find(item => item.slug === 'editar-usuarios')
    const canDelete = permissions.find(item => item.slug === 'borrar-usuarios')
    
    const profileRef = useRef<HTMLSelectElement>(null)
    const activeRef = useRef<HTMLSelectElement>(null)

    const { users, totalUsers, fetchUsers, deleteUser, loading, error } = useUsers();
    const { profiles, fetchProfiles } = useProfiles();

    const [state, setState] = useReducerAsState<State>({
        profileId: -1,
        active: -1,
        limit: 10,
        page: 1,
    })

    const navigate = useNavigate();

    const onPageChange = (page: number) => setState({page})

    const handleCreateUser = () => navigate(`${routes.DASHBOARD}/${routes.USERS}/create`)

    const onDeleteUser = async (userId: number) => {
        if(!canDelete) return

        if(!userId) return

        const confirm = window.confirm('¿Deseas eliminar este usuario? Esta acción no se puede deshacer.')
        if(!confirm) return

        const deleted = await deleteUser(userId)
        toast(deleted ? "Usuario borrado correctamente." : "Ocurrió un error al borrar el usuario", {
            type: deleted ? "success" : "error"
        })

        await fetchUsers({
            profile: state.profileId,
            page: state.page,
            limit: state.limit,
            active: state.active,
        })
    }

    const onFilter = async () =>{
        const profileId = profileRef.current ? parseInt(profileRef.current.value) :  -1
        const active = activeRef.current ? parseInt(activeRef.current.value) :  -1
        await fetchUsers({
            profile: profileId,
            active
        })
    }

    useEffect(() => {
        (async () => {
            await fetchProfiles({
                page: 1,
                limit: 100
            })
            await fetchUsers({
                profile: state.profileId,
                page: state.page,
                limit: state.limit,
                active: state.active,
            })
        })()
        // eslint-disable-next-line
    } ,[state.page])

    return <div className="m-4">
        <div className="flex flex-col mb-4">
            <div className="mb-4">
                <h1>Usuarios</h1>
            </div>
            <div className="flex flex-row justify-between items-end">
                <div className="flex flex-row justify-between items-end space-x-4">
                    <div className="max-w-md">
                        <div className="mb-2 block">
                            <Label htmlFor="profiles" value="Perfíl" />
                        </div>
                        <Select id="profiles" ref={profileRef} required>
                          <option value="-1">- Elija una opción -</option>
                          {profiles && profiles.map((item) => <option key={item.id} value={item.id}>{item.title}</option>)}
                        </Select>
                    </div>
                    <div className="max-w-md">
                        <div className="mb-2 block">
                            <Label htmlFor="active" value="Activo" />
                        </div>
                        <Select id="active" ref={activeRef} required>
                          <option value="-1">- Elija una opción -</option>
                          <option value="1">Si</option>
                          <option value="0">No</option>
                        </Select>
                    </div>
                    <div className="mb-0">
                        <Button onClick={onFilter}>Filtrar</Button>
                    </div>
                </div>
                <div className="flex flex-row">
                    {canCreate && <Button onClick={handleCreateUser}>
                        <HiPlus className="mr-2 h-5 w-5" />
                        Crear usuario
                    </Button>}
                </div>
            </div>
        </div>
        <TableResults
            loading={loading}
            error={error}
            itemsCount={totalUsers}
        >
            <div className="overflow-x-auto mb-4 w-full bg-white border border-gray-200 rounded-lg shadow dark:bg-gray-800 dark:border-gray-700">
                <Table>
                    <Table.Head>
                        <Table.HeadCell>Nombre</Table.HeadCell>
                        <Table.HeadCell>Email</Table.HeadCell>
                        <Table.HeadCell>Perfíl</Table.HeadCell>
                        <Table.HeadCell>Activo</Table.HeadCell>
                        <Table.HeadCell>
                        </Table.HeadCell>
                    </Table.Head>
                    <Table.Body className="divide-y">
                        {users && users.map((item) => <Table.Row key={item.id} className="bg-white dark:border-gray-700 dark:bg-gray-800">
                            <Table.Cell className="whitespace-nowrap font-medium text-gray-900 dark:text-white">
                                {item.display_name}
                            </Table.Cell>
                            <Table.Cell>{item.email}</Table.Cell>
                            <Table.Cell>{item.profile?.title || "No definido"}</Table.Cell>
                            <Table.Cell>{item.active === 1 ? "Si": "No"}</Table.Cell>
                            <Table.Cell>
                                <ButtonGroup>
                                    {canEdit && <Button onClick={() => navigate(`${routes.DASHBOARD}/${routes.USERS}/${item.id}`)} color="gray">
                                        <HiPencil />
                                    </Button>}
                                    {canDelete && <Button onClick={() => onDeleteUser(item.id)} color="failure">
                                        <HiTrash />
                                    </Button>}
                                </ButtonGroup>
                            </Table.Cell>
                        </Table.Row>)}
                    </Table.Body>
                </Table>
            </div>
        </TableResults>
        {users.length > 0 && <div className="flex overflow-x-auto sm:justify-center">
            <Pagination currentPage={state.page || 1} totalPages={Math.ceil(totalUsers / state.limit)} onPageChange={onPageChange} />
        </div>}
    </div>
}

export default UserPage;